import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import { bannerService } from '../../services/bannerService';
import { getImageUrl } from '../../config';

// Interfaz simplificada que coincide con la tabla
interface Banner {
  id: string;
  desktop_image_url: string;
  mobile_image_url: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  link?: string; // <-- Agregar el campo link
}

// Función para convertir RGB a HSL
const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
};

// Función para convertir HSL a RGB
const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

// Función ultra-precisa para extraer color dominante con detección exacta de colores oscuros
const extractDominantColor = (imgElement: HTMLImageElement): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      resolve('#FDBF37');
      return;
    }

    // Usar tamaño grande para máxima precisión
    const maxSize = 400;
    const ratio = Math.min(maxSize / imgElement.naturalWidth, maxSize / imgElement.naturalHeight);
    canvas.width = imgElement.naturalWidth * ratio;
    canvas.height = imgElement.naturalHeight * ratio;
    
    ctx.drawImage(imgElement, 0, 0, canvas.width, canvas.height);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Mapa de colores exactos sin agrupación inicial
    const exactColorMap = new Map<string, number>();
    
    // Analizar TODOS los píxeles sin agrupación para detectar colores exactos
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const alpha = data[i + 3];
      
      // Ignorar píxeles transparentes
      if (alpha < 128) continue;
      
      // Usar colores exactos sin agrupación
      const colorKey = `${r},${g},${b}`;
      exactColorMap.set(colorKey, (exactColorMap.get(colorKey) || 0) + 1);
    }
    
    // Si hay pocos colores únicos, usar el más frecuente directamente
    if (exactColorMap.size <= 50) {
      let dominantColor = '253,159,3';
      let maxCount = 0;
      
      for (const [color, count] of exactColorMap.entries()) {
        if (count > maxCount) {
          maxCount = count;
          dominantColor = color;
        }
      }
      
      const [r, g, b] = dominantColor.split(',').map(Number);
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
      

      
      resolve(`rgb(${r}, ${g}, ${b})`);
      return;
    }
    
    // Para imágenes con muchos colores, agrupar con tolerancia mínima
    const colorGroups = new Map<string, { count: number; avgR: number; avgG: number; avgB: number; colors: number }>();
    
    for (const [colorKey, count] of exactColorMap.entries()) {
      const [r, g, b] = colorKey.split(',').map(Number);
      
      // Agrupación muy fina para preservar colores oscuros
      const tolerance = 5; // Tolerancia muy pequeña
      const rGroup = Math.round(r / tolerance) * tolerance;
      const gGroup = Math.round(g / tolerance) * tolerance;
      const bGroup = Math.round(b / tolerance) * tolerance;
      
      const groupKey = `${rGroup},${gGroup},${bGroup}`;
      
      if (colorGroups.has(groupKey)) {
        const group = colorGroups.get(groupKey)!;
        group.count += count;
        group.avgR = (group.avgR * group.colors + r) / (group.colors + 1);
        group.avgG = (group.avgG * group.colors + g) / (group.colors + 1);
        group.avgB = (group.avgB * group.colors + b) / (group.colors + 1);
        group.colors += 1;
      } else {
        colorGroups.set(groupKey, { count, avgR: r, avgG: g, avgB: b, colors: 1 });
      }
    }
    
    // Encontrar el grupo más frecuente
    let dominantGroup = { avgR: 253, avgG: 159, avgB: 3 };
    let maxCount = 0;
    
    for (const [groupKey, group] of colorGroups.entries()) {
      if (group.count > maxCount) {
        maxCount = group.count;
        dominantGroup = group;
      }
    }
    
    const r = Math.round(dominantGroup.avgR);
    const g = Math.round(dominantGroup.avgG);
    const b = Math.round(dominantGroup.avgB);
    
    const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
    const dominancePercentage = ((maxCount / (canvas.width * canvas.height)) * 100).toFixed(1);
    

    
    resolve(`rgb(${r}, ${g}, ${b})`);
  });
};

const BannerContainer = styled.div<{ backgroundColor?: string }>`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1002;
  overflow: hidden;
  height: 40px;
  background: ${props => props.backgroundColor || '#FDBF37'};
  transition: background-color 0.5s ease;
  
  @media (max-width: 768px) {
    height: 50px; /* Aumentar altura en móvil para mejor visibilidad */
  }
`;

const BannerWrapper = styled.div<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'block' : 'none'};
  animation: ${props => props.isVisible ? 'slideDown 0.15s ease' : 'slideUp 0.15s ease'};
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
`;

const BannerSlide = styled.div<{ isActive: boolean; isVisible: boolean }>`
  width: 100%;
  opacity: ${props => props.isActive && props.isVisible ? 1 : 0};
  visibility: ${props => props.isActive && props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.5s ease-in-out, visibility 0.5s ease-in-out;
  position: ${props => props.isActive ? 'relative' : 'absolute'};
  top: ${props => props.isActive ? 'auto' : '0'};
  left: ${props => props.isActive ? 'auto' : '0'};
  z-index: ${props => props.isActive ? 2 : 1};
`;

const BannerContent = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BannerImage = styled.picture`
  display: block;
  width: 100%;
  
  img {
    width: 100%;
    height: 40px;
    display: block;
    object-fit: contain;
    
    @media (max-width: 768px) {
      height: 50px;
      object-fit: cover; /* Mejor para móvil - ocupa todo el espacio */
      object-position: center; /* Centrar la imagen */
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5); /* Más opaco para mejor visibilidad en móvil */
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); /* Sombra para mejor visibilidad */
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  @media (max-width: 768px) {
    right: 8px;
    width: 28px;
    height: 28px;
    font-size: 13px;
    background: rgba(0, 0, 0, 0.6); /* Más visible en móvil */
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
  }
`;

// Convertir FaTimes a componente React válido
const TimesIcon = FaTimes as React.ComponentType<React.SVGProps<SVGSVGElement>>;

// NUEVO: Props para callback de cierre
export interface PromotionalBannerProps {
  onClose?: () => void;
}

const PromotionalBanner: React.FC<PromotionalBannerProps> = ({ onClose }) => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [closedBanners, setClosedBanners] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [currentBackgroundColor, setCurrentBackgroundColor] = useState<string>('#FDBF37');
  const imageRefs = useRef<{ [key: string]: HTMLImageElement }>({});

  // Función para obtener banners
  const fetchBanners = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await bannerService.getBanners();
      setBanners(response.banners || []);
      
      // Recuperar banners cerrados del localStorage
      const closed = localStorage.getItem('closedBanners');
      if (closed) {
        setClosedBanners(new Set<string>(JSON.parse(closed)));
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      setBanners([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cargar banners al montar el componente
  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  // Filtrar banners activos y no cerrados
  const activeBanners = banners.filter(banner => 
    banner.active && !closedBanners.has(banner.id)
  );

  // Validar y ajustar el índice actual cuando cambian los banners activos
  useEffect(() => {
    if (activeBanners.length > 0 && currentBannerIndex >= activeBanners.length) {
      setCurrentBannerIndex(0);
    }
  }, [activeBanners.length, currentBannerIndex]);

  // Efecto para el carrusel automático - 5 segundos
  useEffect(() => {
    // Solo iniciar carrusel si hay más de un banner
    if (activeBanners.length <= 1 || isLoading) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentBannerIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % activeBanners.length;
        return nextIndex;
      });
    }, 5000); // Cambia cada 5 segundos

    return () => {
      clearInterval(interval);
    };
  }, [activeBanners.length, isLoading]);

  const handleCloseBanner = useCallback(() => {
    // Cerrar TODOS los banners de una vez
    const allBannerIds = banners.map(banner => banner.id);
    const newClosedBanners = new Set<string>(allBannerIds);
    setClosedBanners(newClosedBanners);
    
    // Guardar en localStorage
    localStorage.setItem('closedBanners', JSON.stringify(Array.from(newClosedBanners)));
    
    // Ocultar inmediatamente
    setIsVisible(false);
    onClose?.(); // Llamar al callback de cierre
  }, [banners, onClose]); // Cambiar dependencia de activeBanners a banners

  // Función para manejar cuando una imagen se carga
  const handleImageLoad = useCallback(async (bannerId: string, imgElement: HTMLImageElement) => {
    try {
      const dominantColor = await extractDominantColor(imgElement);
      
      // Solo actualizar el color si este banner es el actual
      const currentBanner = activeBanners[currentBannerIndex];
      if (currentBanner && currentBanner.id === bannerId) {
        setCurrentBackgroundColor(dominantColor);
      }
    } catch (error) {
      console.error('Error extracting color:', error);
    }
  }, [activeBanners, currentBannerIndex]);

  // Efecto para cambiar el color de fondo cuando cambia el banner actual
  useEffect(() => {
    const currentBanner = activeBanners[currentBannerIndex];
    if (currentBanner && imageRefs.current[currentBanner.id]) {
      const imgElement = imageRefs.current[currentBanner.id];
      if (imgElement.complete) {
        // Si la imagen ya está cargada, extraer color inmediatamente
        handleImageLoad(currentBanner.id, imgElement);
      }
    }
  }, [currentBannerIndex, activeBanners, handleImageLoad]);

  // No mostrar nada si está cargando, no hay banners o no es visible
  if (isLoading || activeBanners.length === 0 || !isVisible) {
    return null;
  }

  // Validar que el índice actual sea válido
  const validCurrentIndex = Math.min(currentBannerIndex, activeBanners.length - 1);
  const currentBanner = activeBanners[validCurrentIndex];

  if (!currentBanner) {
    return null;
  }

  return (
    <BannerContainer backgroundColor={currentBackgroundColor}>
      <CarouselContainer>
        {activeBanners.map((banner, index) => (
          <BannerSlide 
            key={`banner-${banner.id}`} 
            isActive={index === validCurrentIndex}
            isVisible={index === validCurrentIndex}
          >
            <BannerContent>
              {banner.link ? (
                <a
                  href={banner.link}
                  rel="noopener noreferrer"
                  style={{ display: 'block', width: '100%', height: '100%' }}
                >
                  <BannerImage>
                    <source 
                      media="(max-width: 680px)" 
                      srcSet={banner.mobile_image_url ? getImageUrl(banner.mobile_image_url) : getImageUrl(banner.desktop_image_url)}
                    />
                    <source 
                      media="(min-width: 681px)" 
                      srcSet={getImageUrl(banner.desktop_image_url)}
                    />
                    <img 
                      ref={(el) => {
                        if (el) {
                          imageRefs.current[banner.id] = el;
                        }
                      }}
                      src={getImageUrl(banner.desktop_image_url)}
                      alt={`Banner promocional ${index + 1}`}
                      loading="lazy"
                      onLoad={(e) => handleImageLoad(banner.id, e.currentTarget)}
                      crossOrigin="anonymous"
                    />
                  </BannerImage>
                </a>
              ) : (
                <BannerImage>
                  <source 
                    media="(max-width: 680px)" 
                    srcSet={banner.mobile_image_url ? getImageUrl(banner.mobile_image_url) : getImageUrl(banner.desktop_image_url)}
                  />
                  <source 
                    media="(min-width: 681px)" 
                    srcSet={getImageUrl(banner.desktop_image_url)}
                  />
                  <img 
                    ref={(el) => {
                      if (el) {
                        imageRefs.current[banner.id] = el;
                      }
                    }}
                    src={getImageUrl(banner.desktop_image_url)}
                    alt={`Banner promocional ${index + 1}`}
                    loading="lazy"
                    onLoad={(e) => handleImageLoad(banner.id, e.currentTarget)}
                    crossOrigin="anonymous"
                  />
                </BannerImage>
              )}
            </BannerContent>
          </BannerSlide>
        ))}
        
        <CloseButton 
          onClick={handleCloseBanner}
          aria-label="Cerrar todos los banners"
        >
          <TimesIcon />
        </CloseButton>
      </CarouselContainer>
    </BannerContainer>
  );
};

export default PromotionalBanner;